export const CONSOLE_PURSUIT_PATH = '/console/pursuit';
export const LEGACY_CONSOLE_CRM_PATH = '/console/crm';
export const ADMIN_CONSOLE_CRM_SECTION_PATH = '/console/admin/crm';
export const CONSOLE_INLET_PATH = '/console/inlet';
export const CONSOLE_RAZZMATAZZ_PATH = '/console/razzmatazz';
export const ADMIN_CONSOLE_OBSERVABILITY_PATH = '/console/admin/observability';
export const ADMIN_CONSOLE_EXPERIENCE_PATH = '/console/admin/experience';

export const PURSUIT_VIEW_IDS = [
  'home',
  'pipeline',
  'leads',
  'accounts',
  'contacts',
  'tasks',
  'website',
  'projects',
  'products',
  'campaigns',
  'guide',
  'quotes',
  'tickets',
  'sequences',
  'insights',
  'reports',
  'territories',
  'desk',
] as const;

export type PursuitViewId = (typeof PURSUIT_VIEW_IDS)[number];

const PURSUIT_VIEW_SET = new Set<string>(PURSUIT_VIEW_IDS);

const matchesPath = (pathname: string, base: string): boolean => (
  pathname === base || pathname.startsWith(`${base}/`)
);

/** Operator Pursuit workspace, including the legacy /console/crm slug. */
export function isPursuitOperatorPath(pathname: string): boolean {
  return matchesPath(pathname, CONSOLE_PURSUIT_PATH) || matchesPath(pathname, LEGACY_CONSOLE_CRM_PATH);
}

/** Operator Inlet workspace for partners and the conversion ledger. */
export function isInletOperatorPath(pathname: string): boolean {
  return matchesPath(pathname, CONSOLE_INLET_PATH);
}

/** Operator Razzmatazz workspace for pages, audits, drafts, reports, and films. */
export function isRazzmatazzOperatorPath(pathname: string): boolean {
  return matchesPath(pathname, CONSOLE_RAZZMATAZZ_PATH) || matchesPath(pathname, '/console/marketing-studio');
}

/** Sidebar CRM via Pursuit highlight, including Access Management /console/admin/crm. */
export function isCrmNavHighlightPath(pathname: string): boolean {
  return isPursuitOperatorPath(pathname) || matchesPath(pathname, ADMIN_CONSOLE_CRM_SECTION_PATH);
}

/** Rewrite leftover /console/crm bookmarks onto /console/pursuit. */
export function canonicalizeConsolePath(pathname: string): string {
  if (matchesPath(pathname, LEGACY_CONSOLE_CRM_PATH)) {
    return `${CONSOLE_PURSUIT_PATH}${pathname.slice(LEGACY_CONSOLE_CRM_PATH.length)}`;
  }
  if (matchesPath(pathname, '/console/marketing-studio')) {
    return CONSOLE_RAZZMATAZZ_PATH;
  }
  return pathname;
}

const operatorBasePath = (pathname: string): string | null => {
  if (matchesPath(pathname, CONSOLE_PURSUIT_PATH)) return CONSOLE_PURSUIT_PATH;
  if (matchesPath(pathname, LEGACY_CONSOLE_CRM_PATH)) return LEGACY_CONSOLE_CRM_PATH;
  return null;
};

/** Inner CRM pane from /console/pursuit/leads (and the legacy /console/crm/leads slug). */
export function pursuitViewFromPath(pathname: string): PursuitViewId {
  const base = operatorBasePath(pathname);
  if (!base) return 'home';
  const rest = pathname.slice(base.length).replace(/^\//, '').split('/')[0] || '';
  if (!rest || rest === 'home') return 'home';
  if (rest === 'reports') return 'insights';
  return PURSUIT_VIEW_SET.has(rest) ? rest as PursuitViewId : 'home';
}

export function pursuitPathForView(view: PursuitViewId): string {
  return view === 'home' ? CONSOLE_PURSUIT_PATH : `${CONSOLE_PURSUIT_PATH}/${view}`;
}

export function pursuitViewBreadcrumbLabel(view: PursuitViewId, language: 'en' | 'es'): string {
  const labels: Record<PursuitViewId, [string, string]> = {
    home: ['Home', 'Inicio'],
    pipeline: ['Pipeline', 'Pipeline'],
    leads: ['Leads', 'Leads'],
    accounts: ['Accounts', 'Cuentas'],
    contacts: ['Contacts', 'Contactos'],
    tasks: ['Tasks', 'Tareas'],
    website: ['Website ingest', 'Ingest web'],
    projects: ['Workspaces', 'Espacios'],
    products: ['Products', 'Productos'],
    campaigns: ['Campaigns', 'Campañas'],
    guide: ['Launch guide', 'Guía de lanzamiento'],
    quotes: ['Quotes', 'Cotizaciones'],
    tickets: ['Tickets', 'Tickets'],
    sequences: ['Sequences', 'Secuencias'],
    insights: ['Insights', 'Perspectivas'],
    reports: ['Reports', 'Reportes'],
    territories: ['Territories', 'Territorios'],
    desk: ['Deal desk', 'Mesa de deals'],
  };
  const pair = labels[view];
  return language === 'es' ? pair[1] : pair[0];
}
